import { skipToken, useQuery } from "@tanstack/react-query";
import {
  getSocialUsersSearch,
  type SocialUserSearchDto,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import {
  HANDLE_MIN_LENGTH,
  normalizeHandleSearchTerm,
} from "Schemas/handleSchema";

const SOCIAL_USER_SEARCH_QUERY_KEY = "SOCIAL_USER_SEARCH";

export const socialUserSearchKeys = {
  all: [SOCIAL_USER_SEARCH_QUERY_KEY] as const,
  byQuery: (query: string) => [...socialUserSearchKeys.all, query] as const,
};

/**
 * Handle-prefix user search ("@" tolerated server-side). Fires from 3 typed characters;
 * callers debounce the input. While a term is being refined (previous and next term
 * are prefix-related), the previous results stay as placeholder data — no flash back
 * to loading on every keystroke. An unrelated term starts fresh: the observer's
 * "previous query" skips no-data keys, so after clearing the input it still points
 * at the last real search, and blanket keepPreviousData would flash those old
 * results into the new search. Exact-term repeats are served from cache (fresh for
 * a minute) — a cache hit always carries the right results for the term, unlike
 * the placeholder, so it can't flash unrelated rows.
 */
export const useSocialUserSearchQuery = (query: string) => {
  const normalizedQuery = normalizeHandleSearchTerm(query);
  const isSearchable = normalizedQuery.length >= HANDLE_MIN_LENGTH;

  return useQuery<Array<SocialUserSearchDto>>({
    queryKey: socialUserSearchKeys.byQuery(normalizedQuery),
    queryFn: isSearchable
      ? processResponseWithApiProblemDetails(() =>
          getSocialUsersSearch(normalizedQuery),
        )
      : skipToken,
    placeholderData: isSearchable
      ? (previousData, previousQuery) => {
          const previousTerm = previousQuery?.queryKey[1];
          const currentLower = normalizedQuery.toLowerCase();
          const previousLower =
            typeof previousTerm === "string"
              ? previousTerm.toLowerCase()
              : undefined;
          const isRefinement =
            previousLower !== undefined &&
            (currentLower.startsWith(previousLower) ||
              previousLower.startsWith(currentLower));
          return isRefinement ? previousData : undefined;
        }
      : undefined,
    staleTime: 60 * 1000,
  });
};
